import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Monitor, LogOut, RefreshCw } from "lucide-react";

/** Parse the User-Agent string into a friendly label. Best-effort —
 * missing/odd UAs fall back to "Unknown device". */
function _labelForUA(ua) {
  if (!ua) return "Unknown device";
  const u = ua.toLowerCase();
  const os =
    u.includes("mac os") || u.includes("macintosh") ? "macOS" :
    u.includes("windows") ? "Windows" :
    u.includes("iphone") ? "iPhone" :
    u.includes("ipad") ? "iPad" :
    u.includes("android") ? "Android" :
    u.includes("linux") ? "Linux" : null;
  const browser =
    u.includes("edg/") ? "Edge" :
    u.includes("chrome") && !u.includes("edg") ? "Chrome" :
    u.includes("firefox") ? "Firefox" :
    u.includes("safari") && !u.includes("chrome") ? "Safari" : "Browser";
  return os ? `${browser} · ${os}` : browser;
}

function _relTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const secs = Math.round((Date.now() - d.getTime()) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)} hr ago`;
  return d.toLocaleString();
}

export function SessionsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/sessions");
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load sessions.");
    }
  };

  useEffect(() => { load(); }, []);

  const revokeOne = async (jti) => {
    setBusy(true);
    try {
      await api.delete(`/auth/sessions/${jti}`);
      toast.success("Session signed out.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not revoke session.");
    } finally {
      setBusy(false);
    }
  };

  const revokeOthers = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/auth/sessions/others");
      toast.success(
        data.revoked === 0
          ? "No other sessions to sign out."
          : `Signed out ${data.revoked} other session${data.revoked === 1 ? "" : "s"}.`,
      );
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not revoke sessions.");
    } finally {
      setBusy(false);
    }
  };

  if (data === null) {
    return (
      <div className="mt-6 rounded-xl border border-black/5 bg-white p-6" data-testid="sessions-loading">
        <p className="text-sm text-ek-ink/60">Loading active sessions…</p>
      </div>
    );
  }

  if (!data.tracked) {
    return (
      <div className="mt-6 rounded-xl border border-black/5 bg-white p-6" data-testid="sessions-panel-untracked">
        <div className="flex items-start gap-3">
          <Monitor size={18} className="mt-0.5 text-ek-ink/60" />
          <div>
            <p className="font-display text-base font-semibold text-ek-ink">Active sessions</p>
            <p className="mt-0.5 text-sm text-ek-ink/65" data-testid="sessions-untracked-note">
              {data.note}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const sessions = data.sessions || [];

  return (
    <div className="mt-6 rounded-xl border border-black/5 bg-white p-6" data-testid="sessions-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-display text-base font-semibold text-ek-ink">Active sessions</p>
          <p className="mt-0.5 text-sm text-ek-ink/65">
            Devices currently signed into your account. Sign out any you don&apos;t recognise.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            data-testid="sessions-refresh"
            className="inline-flex items-center gap-1.5 rounded-lg border border-black/10 bg-white px-3 py-1.5 text-xs font-medium hover:bg-ek-purple/5"
            aria-label="Refresh sessions"
          >
            <RefreshCw size={13} /> Refresh
          </button>
          {sessions.length > 1 && (
            <button
              type="button"
              onClick={revokeOthers}
              disabled={busy}
              data-testid="sessions-revoke-others"
              className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              <LogOut size={13} /> Sign out other devices
            </button>
          )}
        </div>
      </div>

      <ul className="mt-4 divide-y divide-black/5" data-testid="sessions-list">
        {sessions.map((s) => (
          <li key={s.jti} className="flex items-center justify-between gap-3 py-3" data-testid={`session-row-${s.jti}`}>
            <div className="flex min-w-0 items-start gap-3">
              <Monitor size={18} className="mt-0.5 shrink-0 text-ek-ink/50" />
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ek-ink" data-testid={`session-label-${s.jti}`}>
                  {_labelForUA(s.user_agent)}
                  {s.is_current && (
                    <span
                      className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700"
                      data-testid={`session-current-${s.jti}`}
                    >
                      This device
                    </span>
                  )}
                </p>
                <p className="mt-0.5 text-xs text-ek-ink/55">
                  {s.ip ? `IP ${s.ip}` : "IP unknown"} · Last active {_relTime(s.last_activity_at)}
                </p>
              </div>
            </div>
            {!s.is_current && (
              <button
                type="button"
                onClick={() => revokeOne(s.jti)}
                disabled={busy}
                data-testid={`session-revoke-${s.jti}`}
                className="shrink-0 rounded-lg border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-60"
              >
                Sign out
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
